// aiUsage / format — numbers, money, durations and labels for the AI-usage
// pages. Pure: numbers are formatted for the given locale with `Intl`;
// labels are Lingui `MessageDescriptor`s the components localize.
//
// Money arrives as integer micro-USD; null means unpriced and is never
// shown as 0. Big numbers abbreviate (12.4M) and the exact value goes in a
// tooltip. Every call site and outcome has a label, unknown ones included.

export {
    breakdownLabel,
    cacheHitRate,
    callTypeLabel,
    costFilterLabel,
    exactMoney,
    formatCount,
    formatDay,
    formatDayTime,
    formatInt,
    formatMoney,
    formatPercent,
    formatRange,
    formatSeconds,
    moneyIsAbbreviated,
    outcomeFilterLabel,
    outcomeLabel,
    outcomeTone,
    siteDetailLabel,
    totalTokens,
};

import type {
    AiUsageBreakdownBy,
    AiUsageCostFilter,
    AiUsageOutcome,
    AiUsageOutcomeFilter,
    AiUsageSite,
    AiUsageTotals,
    aiUsageSiteTypes,
} from "@bilda/app/agentCallAdmin";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

// === Numbers ===

const formatInt = (locale: string, n: number): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);

// Abbreviated past ten thousand (182.4M); exact below.
const formatCount = (locale: string, n: number): string =>
    Math.abs(n) < 10_000
        ? formatInt(locale, n)
        : new Intl.NumberFormat(locale, {
              notation: "compact",
              maximumFractionDigits: 1,
          }).format(n);

const usd = (locale: string, digits: number) =>
    new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

// Below a cent shows "< $0.01"; zero shows "$0.00". `precise` keeps three
// decimals (a single call's cost lines).
const formatMoney = (
    locale: string,
    micro: number,
    precise = false,
): string => {
    const dollars = micro / 1_000_000;
    if (precise) return usd(locale, 3).format(dollars);
    if (micro > 0 && dollars < 0.01) return `< ${usd(locale, 2).format(0.01)}`;
    if (Math.abs(dollars) >= 1_000_000)
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "USD",
            notation: "compact",
            maximumFractionDigits: 2,
        }).format(dollars);
    return usd(locale, 2).format(dollars);
};

const moneyIsAbbreviated = (micro: number): boolean =>
    Math.abs(micro) >= 1_000_000_000_000 || (micro > 0 && micro < 10_000);

const exactMoney = (locale: string, micro: number): string =>
    usd(locale, 2).format(micro / 1_000_000);

const formatSeconds = (locale: string, ms: number): string =>
    `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(ms / 1000)} s`;

const formatPercent = (locale: string, share: number): string =>
    new Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 0,
    }).format(share);

// "19 Oct 10:42:08" (locale order), 24-hour, seconds kept.
const formatDayTime = (locale: string, iso: string): string => {
    const d = new Date(iso);
    const date = new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
    }).format(d);
    const time = new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).format(d);
    return `${date} ${time}`;
};

// A calendar day ("2026-10-14") as "14 Oct", read as a local date.
const formatDay = (locale: string, day: string): string => {
    const [y, m, d] = day.split("-").map((x) => Number.parseInt(x, 10));
    return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
    }).format(new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1));
};

const formatRange = (locale: string, from: Date, last: Date): string =>
    new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
    }).formatRange(from, last);

// === Totals ===

const totalTokens = (t: {
    readonly inputTokens: number;
    readonly outputTokens: number;
    readonly cacheReadTokens: number;
    readonly cacheWriteTokens: number;
}): number =>
    t.inputTokens + t.outputTokens + t.cacheReadTokens + t.cacheWriteTokens;

// Share of all input read from cache; undefined with no input at all.
const cacheHitRate = (t: AiUsageTotals): number | undefined => {
    const input = t.inputTokens + t.cacheReadTokens + t.cacheWriteTokens;
    return input === 0 ? undefined : t.cacheReadTokens / input;
};

// === Labels ===

type CallType = (typeof aiUsageSiteTypes)[number];

const callTypeLabel = (type: CallType | "unknown_site"): MessageDescriptor => {
    switch (type) {
        case "chat_round":
            return msg`Chat round`;
        case "reply_check":
            return msg`Reply check`;
        case "ocr_batch":
            return msg`Page reading (OCR)`;
        case "source_understanding":
            return msg`Source reading`;
        case "part_generation":
            return msg`Part generation`;
        case "judged_checks":
            return msg`Quality check`;
        case "claim_reading":
            return msg`Fact reading`;
        case "material_repair":
            return msg`Material repair`;
        case "journal_summary":
            return msg`Journal summary`;
        case "activity_summary":
            return msg`Activity summary`;
        case "material_draft":
            return msg`Material draft`;
        case "freeform_structuring":
            return msg`Quiz structuring`;
        case "unknown_site":
            return msg`Unknown call type`;
    }
};

// The call type plus where in its work the call sits.
const siteDetailLabel = (site: AiUsageSite): MessageDescriptor => {
    switch (site.type) {
        case "chat_round":
            return msg`Chat · round ${site.round + 1}`;
        case "reply_check":
            return msg`Reply check`;
        case "ocr_batch": {
            const first = Math.min(...site.pages);
            const last = Math.max(...site.pages);
            return site.pages.length === 0
                ? msg`Page reading (OCR)`
                : first === last
                  ? msg`OCR · page ${first}`
                  : msg`OCR · pages ${first}–${last}`;
        }
        case "source_understanding":
            return msg`Source reading · ${site.pass.type}`;
        case "part_generation":
            return msg`Part generation · proposal ${site.proposal}`;
        case "judged_checks":
            return msg`Quality check · ${site.partIds.length} parts`;
        case "claim_reading":
            return msg`Fact reading`;
        case "material_repair":
            return msg`Material repair · round ${site.iteration}`;
        case "journal_summary":
            return msg`Journal summary · ${site.period}`;
        case "activity_summary":
            return msg`Activity summary`;
        case "material_draft":
            return msg`Material draft`;
        case "freeform_structuring":
            return msg`Quiz structuring · attempt ${site.attempt}`;
        case "unknown_site":
            return msg`Unknown · ${site.label}`;
    }
};

const errorKindLabel = (error: string): MessageDescriptor => {
    switch (error) {
        case "timeout":
            return msg`Error · timeout, no response`;
        case "rate_limited":
            return msg`Error · rate limited`;
        case "overloaded":
            return msg`Error · overloaded`;
        case "auth":
            return msg`Error · not authorized`;
        case "invalid_request":
            return msg`Error · invalid request`;
        case "context_window_exceeded":
            return msg`Error · context too long`;
        case "quota_exhausted":
            return msg`Error · quota used up`;
        case "usage_limit_reached":
            return msg`Error · usage limit reached`;
        case "network":
            return msg`Error · network`;
        case "response_malformed":
            return msg`Error · unreadable response`;
        default:
            return msg`Error · ${error}`;
    }
};

const outcomeLabel = (
    outcome: AiUsageOutcome,
    toolCalls: number,
): MessageDescriptor => {
    switch (outcome.type) {
        case "call_completed": {
            if (outcome.stop.type === "stop_unknown")
                return msg`Ok · unknown stop reason`;
            switch (outcome.stop.reason) {
                case "end_turn":
                    return msg`Ok · end of turn`;
                case "tool_use":
                    return msg`Ok · tool use (${toolCalls})`;
                case "max_tokens":
                    return msg`Ok · hit max tokens`;
                case "stop_sequence":
                    return msg`Ok · stop sequence`;
                case "pause_turn":
                    return msg`Ok · paused`;
                case "refusal":
                    return msg`Ok · refused`;
                default:
                    return msg`Ok · ${outcome.stop.raw}`;
            }
        }
        case "call_partial":
            return msg`Partial · stream broke`;
        case "call_failed":
            return errorKindLabel(outcome.error);
        case "call_cancelled":
            return msg`Cancelled`;
        case "outcome_unknown":
            return msg`Unknown · sent as ${outcome.raw}`;
    }
};

type OutcomeTone = "success" | "warning" | "danger" | "neutral";

const outcomeTone = (outcome: AiUsageOutcome): OutcomeTone => {
    switch (outcome.type) {
        case "call_completed":
            if (outcome.stop.type === "stop_unknown") return "neutral";
            return outcome.stop.reason === "max_tokens" ||
                outcome.stop.reason === "refusal"
                ? "warning"
                : "success";
        case "call_partial":
            return "warning";
        case "call_failed":
            return "danger";
        case "call_cancelled":
        case "outcome_unknown":
            return "neutral";
    }
};

const outcomeFilterLabel = (o: AiUsageOutcomeFilter): MessageDescriptor => {
    switch (o) {
        case "completed":
            return msg`Ok`;
        case "partial":
            return msg`Partial`;
        case "failed":
            return msg`Error`;
        case "cancelled":
            return msg`Cancelled`;
        case "not_completed":
            return msg`Not ok`;
    }
};

const costFilterLabel = (c: AiUsageCostFilter): MessageDescriptor => {
    switch (c) {
        case "unpriced":
            return msg`Unknown cost`;
        case "at_least_10c":
            return msg`$0.10 or more`;
        case "at_least_1usd":
            return msg`$1 or more`;
    }
};

const breakdownLabel = (by: AiUsageBreakdownBy): MessageDescriptor => {
    switch (by) {
        case "workspace":
            return msg`Workspace`;
        case "user":
            return msg`User`;
        case "model":
            return msg`Model`;
        case "call_type":
            return msg`Call type`;
    }
};
